import { Box, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import { EligibleRegisterGroup } from '@schooly/api';
import { FilterButton, SearchInput } from '@schooly/components/filters';
import { ATTENDANCE, buildTestId } from '@schooly/constants/test-ids';
import { useFlag } from '@schooly/hooks/use-flag';
import {
  Counter,
  CrossIcon,
  DropdownIcon,
  FilterIcon,
  Bubble,
  Loading,
  ModalContent,
} from '@schooly/style';
import React, { FC, PropsWithChildren, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import { NoSearchResultsFound } from '../../../components/common/NoSearchResultsFound/NoSearchResultsFound';
import { ModalHeader } from '../../../components/uikit-components/Modal/ModalHeader';
import { TimetableGroupCard } from '../../School/SchoolTimetables/CreateTimetable/AddGroupModal';
import {
  emptyRegisterGroupsFilters,
  filterRegisterGroups,
  getRegisterGroupsFilterOptions,
  hasActiveFilters,
  RegisterGroupsFilters,
  sanitizeRegisterGroupsFilters,
} from './RegisterFilters/utils';
import {
  RegisterGroupsFilterDialog,
  RegisterGroupsInlineFilterTags,
} from './RegisterFilters/RegisterGroupsFilterDialog';
import { RegisterGroupIds, RegisterPeriod, RegisterScope } from './types';
import { getAddedStructures, getBlockingStructure } from './utils/registerCoupling';
import { getTotalStudentCount } from './utils/registerCounts';
import { getGroupIdsForScope } from './utils/registerGroupIds';
import { buildSidebarSections } from './utils/registerRoster';

type RegisterGroupsSidebarProps = {
  badgeLabel: string;
  eligibleGroups: EligibleRegisterGroup[];
  addedGroupIds: RegisterGroupIds;
  periods: RegisterPeriod[];
  initialRosterIds: string[];
  isEdit: boolean;
  loading: boolean;
  onSelectGroupId: (id: string) => void;
  scope: RegisterScope;
};

type CollapsedSectionProps = PropsWithChildren<{
  title: string;
  counter: number;
  initialExpanded: boolean;
}>;

const CollapsedSection: FC<CollapsedSectionProps> = ({
  title,
  counter,
  initialExpanded,
  children,
}) => {
  const [expanded, setExpanded] = useState(initialExpanded);

  return (
    <Stack flex="0 0 auto">
      <Stack
        onClick={() => setExpanded((value) => !value)}
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        width="100%"
        sx={{ cursor: 'pointer' }}
      >
        <Stack direction="row" alignItems="center" gap={0.5} minWidth={0}>
          <Typography color={expanded ? 'primary.main' : 'common.grey'} variant="h4" noWrap>
            {title}
          </Typography>
          <Counter
            disabled
            sx={(theme) => ({
              minWidth: 16,
              maxHeight: 16,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              ...(!expanded && { borderColor: theme.palette.common.light2 }),
            })}
          >
            <Typography variant="body3" color={expanded ? 'primary.main' : 'common.grey'}>
              {counter}
            </Typography>
          </Counter>
        </Stack>
        <IconButton
          sx={(theme) => ({
            color: expanded ? undefined : theme.palette.common.grey,
            transform: expanded ? 'rotate(180deg)' : 'none',
            transition: theme.transitions.create(['all'], {
              easing: theme.transitions.easing.easeOut,
              duration: theme.transitions.duration.short,
            }),
          })}
        >
          <DropdownIcon />
        </IconButton>
      </Stack>
      {expanded && <Box mt={1}>{children}</Box>}
    </Stack>
  );
};

export const RegisterGroupsSidebar: FC<RegisterGroupsSidebarProps> = ({
  badgeLabel,
  eligibleGroups,
  addedGroupIds,
  periods,
  initialRosterIds,
  isEdit,
  loading,
  onSelectGroupId,
  scope,
}) => {
  const { formatMessage } = useIntl();
  const [query, setQuery] = useState('');
  const [selectedFilters, setSelectedFilters] = useState<RegisterGroupsFilters>(
    emptyRegisterGroupsFilters,
  );
  const [filtersModalVisible, openFilterModal, closeFiltersModal] = useFlag(false);

  const { available, explicitlyRemoved } = useMemo(
    () =>
      buildSidebarSections({
        eligibleGroups,
        scope,
        localRosterIds: getGroupIdsForScope(addedGroupIds, scope),
        initialRosterIds,
        isEdit,
      }),
    [eligibleGroups, scope, addedGroupIds, initialRosterIds, isEdit],
  );

  const sidebarGroups = useMemo(
    () => [...available, ...explicitlyRemoved],
    [available, explicitlyRemoved],
  );

  const filterOptions = useMemo(
    () => getRegisterGroupsFilterOptions(sidebarGroups),
    [sidebarGroups],
  );

  const filters = useMemo(
    () => sanitizeRegisterGroupsFilters(selectedFilters, filterOptions),
    [selectedFilters, filterOptions],
  );

  const filteredAvailable = useMemo(
    () => filterRegisterGroups(available, query, filters),
    [available, query, filters],
  );

  const filteredRemoved = useMemo(
    () => filterRegisterGroups(explicitlyRemoved, query, filters),
    [explicitlyRemoved, query, filters],
  );

  const hasFiltersApplied = hasActiveFilters(filters);

  const isEmpty = !filteredAvailable.length && !filteredRemoved.length;

  const searchableGroupCount = useMemo(
    () => filterRegisterGroups(sidebarGroups, '', filters).length,
    [sidebarGroups, filters],
  );

  const addedStructuresByGroupId = useMemo(
    () => getAddedStructures(addedGroupIds, periods),
    [addedGroupIds, periods],
  );

  const renderGroupCard = (group: EligibleRegisterGroup) => {
    const groupCard = {
      id: group.group_id,
      name: group.name,
      student_count: getTotalStudentCount(group),
      type: group.group_type,
      subject: group.subject ? { id: group.subject.id, name: group.subject.name } : null,
    };
    const blockingStructure = getBlockingStructure({
      groupId: group.group_id,
      addedStructures: addedStructuresByGroupId,
      scope,
      periods,
    });

    return (
      <Tooltip
        key={group.group_id}
        title={
          blockingStructure
            ? formatMessage(
                { id: 'attendance-GroupAlreadyAddedToStructure' },
                { name: blockingStructure.name },
              )
            : ''
        }
        disableHoverListener={!blockingStructure}
      >
        <Box
          data-test-id={buildTestId(ATTENDANCE.ADD_GROUP_CARD_PREFIX, group, 'name', 'group_id')}
        >
          <TimetableGroupCard
            group={groupCard}
            disabled={!!blockingStructure}
            onClick={() => onSelectGroupId(group.group_id)}
          />
        </Box>
      </Tooltip>
    );
  };

  const renderContent = () => {
    if (loading) {
      return <Loading />;
    }

    if (isEmpty) {
      return <NoSearchResultsFound type="small" />;
    }

    return (
      <Stack gap={2} pt={1}>
        {!!filteredRemoved.length && (
          <CollapsedSection
            initialExpanded
            counter={filteredRemoved.length}
            title={formatMessage({ id: 'attendance-ExplicitlyRemoved' })}
          >
            <Stack>{filteredRemoved.map((group) => renderGroupCard(group))}</Stack>
          </CollapsedSection>
        )}
        {!!filteredAvailable.length && (
          <CollapsedSection
            initialExpanded
            counter={filteredAvailable.length}
            title={formatMessage({ id: 'attendance-AvailableGroups' })}
          >
            <Stack>{filteredAvailable.map((group) => renderGroupCard(group))}</Stack>
          </CollapsedSection>
        )}
      </Stack>
    );
  };

  return (
    <Stack sx={{ height: '100%' }}>
      <ModalHeader active title={formatMessage({ id: 'groups-Group-plural' })}>
        <Bubble disableHover label={badgeLabel} sx={{ borderRadius: 0.5 }} />
      </ModalHeader>
      <Box px={2.5} pt={2.5}>
        <Stack justifyContent="center" position="relative">
          <SearchInput
            placeholder={formatMessage(
              { id: 'groups-SearchAmongCount' },
              {
                GroupPlural: `${searchableGroupCount} ${formatMessage({
                  id: 'assessments-Groups',
                }).toLowerCase()}`,
              },
            )}
            onChangeText={setQuery}
            value={query}
            canClear={false}
            InputProps={{
              endAdornment: (
                <Stack pl={0.75} gap={0.5} pr={1} flexDirection="row" alignItems="center">
                  {query ? (
                    <IconButton
                      sx={(theme) => ({ '&:hover': { color: theme.palette.text.primary } })}
                      inverse
                      onClick={() => setQuery('')}
                    >
                      <CrossIcon />
                    </IconButton>
                  ) : null}
                  <FilterButton
                    hasFiltersApplied={hasFiltersApplied}
                    onClick={openFilterModal}
                    sx={(theme) => ({
                      '.svg-icon path': {
                        fill: hasFiltersApplied ? 'unset' : theme.palette.common.grey,
                      },
                      '&:hover .svg-icon path': {
                        fill: theme.palette.primary.main,
                      },
                    })}
                  >
                    <FilterIcon />
                  </FilterButton>
                </Stack>
              ),
            }}
          />
        </Stack>
        <Box pt={1}>
          <RegisterGroupsInlineFilterTags filters={filters} onOpenFilters={openFilterModal} />
        </Box>
      </Box>

      <ModalContent active sx={{ flex: 1, minHeight: 0, pt: 1.5 }}>
        {renderContent()}
      </ModalContent>

      {filtersModalVisible && (
        <RegisterGroupsFilterDialog
          filters={filters}
          options={filterOptions}
          onClose={closeFiltersModal}
          onApply={setSelectedFilters}
        />
      )}
    </Stack>
  );
};
